import { useCallback } from 'react';
import { toast } from 'sonner';
import { Client } from '@/components/clients/types';
import { useCompanyEnrichment, TriggerEnrichmentPayload, EnrichmentResponse } from '@/hooks/useCompanyEnrichment';
import { BackendProgressStage } from '@/components/clients/enrichment-dialog/enrichmentTypes';

interface UseEnrichmentTriggerProps {
  setLastEnrichmentData: (data: Partial<EnrichmentResponse> | null) => void;
  setLastRunId: (runId: string) => void;
  refetchClient: () => Promise<void> | void;
  lastRunIdToMaintain?: string | undefined;
}

export const useEnrichmentTrigger = ({
  setLastEnrichmentData,
  setLastRunId,
  refetchClient,
  lastRunIdToMaintain,
}: UseEnrichmentTriggerProps) => {
  const { triggerEnrichmentMutation, isPending: isEnrichmentTriggering } = useCompanyEnrichment();

  const triggerNewEnrichmentFlow = useCallback(async (client: Client) => {
    if (!client) return;

    toast.info(`Initiating enrichment for ${client.name}...`, {
      description: "You'll see real-time updates as the process progresses."
    });

    setLastEnrichmentData({
      progress_stage: 'requested' as BackendProgressStage,
      status: 'pending',
      lutra_run_id: lastRunIdToMaintain,
    });

    try {
      const payload: TriggerEnrichmentPayload = {
        client_id: client.id,
        company_name: client.name,
        company_website: client.website || undefined,
      };
      const result: EnrichmentResponse = await triggerEnrichmentMutation(payload);

      setLastEnrichmentData(result);

      if (result.error_message) {
        const details = result.details || '';
        toast.error(`Enrichment Error: ${result.error_message}${details ? ` - ${details}` : ''}`);
      } else {
        const message = result.message || `Enrichment for ${client.name} initiated successfully.`;
        toast.success(message, {
          description: result.progress_stage ? `Stage: ${result.progress_stage}` : "We'll start checking for results."
        });
        if (result.lutra_run_id) {
          setLastRunId(result.lutra_run_id);
        }
      }
      await refetchClient();
    } catch (error: any) {
      const errorMessage = error.message || "An unexpected error occurred";
      setLastEnrichmentData({ error_message: errorMessage, progress_stage: 'error_initiating' as BackendProgressStage, status: 'error' });
      toast.error(`Failed to trigger enrichment: ${errorMessage}`);
      console.error("Enrichment trigger error:", error);
      await refetchClient();
    }
  }, [
    triggerEnrichmentMutation,
    setLastEnrichmentData,
    setLastRunId,
    refetchClient,
    lastRunIdToMaintain,
  ]);

  return {
    triggerNewEnrichmentFlow,
    isEnrichmentTriggering,
  };
};
